import { Icon, Text } from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { Translate } from "../i18n/Translate";
import { NoAccessVariant } from "../session/controls";
import {
  faLock,
  faMicrophone,
  faVideo,
} from "@fortawesome/pro-solid-svg-icons";
import { TFunction } from "i18next";

function getDesktopNoAccessMessage(
  t: TFunction,
  noAccessVariant: NoAccessVariant | undefined,
  isSafari: boolean,
): string {
  if (isSafari) {
    switch (noAccessVariant) {
      case NoAccessVariant.Microphone:
        return t(
          "telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicNavigateToSafariSettings",
        );
      case NoAccessVariant.Camera:
        return t(
          "telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraNavigateToSafariSettings",
        );
      default:
        return t(
          "telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAndMicNavigateToSafariSettings",
        );
    }
  }
  switch (noAccessVariant) {
    case NoAccessVariant.Microphone:
      return t(
        "telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicSelectSettingsIcon",
      );
    case NoAccessVariant.Camera:
      return t(
        "telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraSelectSettingsIcon",
      );
    default:
      return t(
        "telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAndMicSelectSettingsIcon",
      );
  }
}

export function BlockedPermissionDescription({
  isMobile,
  isSafari,
  noAccessVariant,
}: {
  isMobile: boolean;
  isSafari: boolean;
  noAccessVariant?: NoAccessVariant;
}) {
  const t = useTranslate();
  const mediaIcon =
    noAccessVariant === NoAccessVariant.Microphone ? faMicrophone : faVideo;

  const iconComponents = {
    1: <Icon icon={mediaIcon} size="xs" aria-hidden />,
    3: <Icon icon={faLock} size="xs" aria-hidden />,
  };

  return isMobile ? (
    <>
      <Text
        className="text-neutral-800"
        data-testid="blocked-permissions.mobile-instructions-section"
        variant="md"
      >
        {noAccessVariant === NoAccessVariant.Microphone ? (
          <Translate
            i18nKey="telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourMicAllowAccessBySelecting"
            components={iconComponents}
          />
        ) : noAccessVariant === NoAccessVariant.Camera ? (
          <Translate
            i18nKey="telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAllowAccessBySelecting"
            components={iconComponents}
          />
        ) : (
          <Translate
            i18nKey="telehealth-core:permissions.yourBrowserDoesntHaveAccessToYourCameraAndMicAllowAccessBySelecting"
            components={iconComponents}
          />
        )}
      </Text>
      <section data-testid="blocked-permissions.additional-info-section">
        <Text as="h3" className="font-medium">
          {t("telehealth-core:permissions.ifThatDidntWork")}
        </Text>
        <ul className="list-disc ml-4">
          <li>
            <Text variant="md" className="text-neutral-800 mb-4">
              <Text
                className="text-lilac-700 font-medium"
                as="span"
                variant="md"
              >
                {t("telehealth-core:permissions.forIphone")}
              </Text>{" "}
              {t(
                "telehealth-core:permissions.openYourDevicesSettingsPrivacyAndSecurity",
              )}
            </Text>
          </li>
          <li>
            <Text variant="md" className="text-neutral-800">
              <Text
                className="text-lilac-700 inline font-medium"
                as="span"
                variant="md"
              >
                {t("telehealth-core:permissions.forAndroid")}
              </Text>{" "}
              {t(
                "telehealth-core:permissions.openYourDevicesSettingsSecurityAndPrivacy",
              )}
            </Text>
          </li>
        </ul>
      </section>
    </>
  ) : (
    <>
      <Text
        className="text-medium text-neutral-800"
        data-testid="blocked-permissions.instructions-section"
        variant="md"
      >
        {getDesktopNoAccessMessage(t, noAccessVariant, isSafari)}
      </Text>
    </>
  );
}
